import { useRef } from 'react';

const ConformanceSetup = ({
  objectType,
  setObjectType,
  objectTypes,
  referenceFile,
  referenceKey,
  uploadingRef,
  uploadError,
  onReferenceFileChange,
  onOpenFilePicker,
  onRun,
  loading,
  runLabel = 'Run Conformance Check',
}) => {
  const fileInputRef = useRef(null);
  const canRun = objectType && referenceKey && !loading && !uploadingRef;

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) onReferenceFileChange(file);
    e.target.value = '';
  };

  return (
    <div className="flex flex-col gap-6 p-4 max-w-lg mx-auto w-full">
      <p className="text-sm text-gray-500">
        Select the object type to analyze, then upload the reference OCEL file that
        defines your normative process (Soll-Prozess).
      </p>

      <div className="flex flex-col gap-4">
        {/* Object type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Object Type
          </label>
          <select
            value={objectType}
            onChange={(e) => setObjectType(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select object type…</option>
            {objectTypes.map((ot) => (
              <option key={ot} value={ot}>{ot}</option>
            ))}
          </select>
        </div>

        {/* Reference OCEL upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reference OCEL File (Soll-Prozess)
          </label>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileInput}
            className="hidden"
          />

          {uploadingRef || (referenceFile && referenceKey) ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingRef}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300
                rounded-md px-4 py-4 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600
                disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer bg-white"
            >
              {uploadingRef ? (
                <>
                  <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  Parsing OCEL…
                </>
              ) : (
                <>
                  <span className="text-green-600">✓</span>
                  <span className="text-green-700 font-medium truncate max-w-[260px]">{referenceFile.name}</span>
                  <span className="text-gray-400 text-xs">(click to replace)</span>
                </>
              )}
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-gray-300
                  rounded-md px-3 py-4 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600
                  transition-colors cursor-pointer bg-white"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Upload new (.json)
              </button>

              {onOpenFilePicker && (
                <button
                  type="button"
                  onClick={onOpenFilePicker}
                  className="flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-gray-300
                    rounded-md px-3 py-4 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600
                    transition-colors cursor-pointer bg-white"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  Select existing
                </button>
              )}
            </div>
          )}

          {uploadError && (
            <p className="text-xs text-red-600 mt-1">{uploadError}</p>
          )}
          {referenceFile && !referenceKey && !uploadingRef && !uploadError && (
            <p className="text-xs text-amber-600 mt-1">Upload incomplete</p>
          )}
        </div>
      </div>

      <button
        onClick={onRun}
        disabled={!canRun}
        className="mt-2 w-full py-2 px-4 bg-blue-600 text-white text-sm font-medium rounded-md
          hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? 'Running…' : runLabel}
      </button>
    </div>
  );
};

export default ConformanceSetup;
